/* Phase 2Z V1 — Rota Style Automation settings page.

   The ONLY V1 automation surface. Thin wrapper around the canonical
   Rota Style draft generation flow (`_rsb_seed_draft_period`). No
   gap engine, no scoring, no maturity levels.

   R3 (Feb 2026) - Added an outer plan gate so that non-Command
   Workspace Admins arriving via direct URL see a locked card
   symmetric to the Command-only button gate that hides
   "Configure Automation" on `/app/rota-style`. Also added two small
   guidance blocks (Automation V1 + Leave automation) INSIDE this
   page. The main Rota Style page never describes these. */

import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { hasCommandAccess } from "@/lib/effectivePlan";
import {
  Loader2,
  AlertTriangle,
  Info,
  ArrowRight,
  CheckCircle2,
  Zap,
  CalendarCheck,
  Save as SaveIcon,
} from "lucide-react";

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday",
                     "Friday", "Saturday", "Sunday"];

function humanDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function humanDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}


// R3 (Feb 2026) - Outer plan gate. Non-Command Workspace Admins who
// arrive by direct URL see a locked card. Trial / founder / override
// / Command all pass through. The role guard on the route stays as
// company_admin / super_admin (unchanged in App.js).
export default function RotaStyleAutomationPage() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/billing/me").then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);
  if (me && !hasCommandAccess(me)) {
    return (
      <LockedFeatureCard
        testid="rsa-locked-non-command"
        badge="COMMAND"
        title="Automation is available on Command"
        body="Automation creates draft rosters from your Rota Style on a schedule. It is available on Command plans. You can still build and publish rosters manually from the Roster page."
        primary={{ to: "/app/billing", label: "Upgrade to Command" }}
        secondary={{ to: "/app/rota-style", label: "Open Rota Style" }}
      />
    );
  }
  if (!me) return null;  // brief loading sliver
  return <RotaStyleAutomationPageInner />;
}


// TP-RSA.visibility (Feb 2026) -- inline Save icon lives at
// module scope so React does not remount it on every parent
// render (satisfies react/no-unstable-nested-components) and so
// tests can locate it by data-testid. Same handler as the primary
// footer Save; the parent passes `onSave` + `saving` in.
function InlineSave({ testid, onSave, saving }) {
  return (
    <div className="mt-2 flex justify-end">
      <Button type="button" variant="outline" size="sm"
              onClick={onSave} disabled={saving}
              aria-label="Save"
              data-testid={testid}
              className="h-8 px-2">
        {saving
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <SaveIcon className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}


function RotaStyleAutomationPageInner() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  // Editable local copy of settings -- flushed on Save.
  const [draft, setDraft] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/automation/rota-style/settings");
      setState(data);
      setDraft(data.settings);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(
        "/automation/rota-style/settings", draft);
      setState(data);
      setDraft(data.settings);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  // TP-RSA.visibility (Feb 2026) -- inline Save icon hoisted out of
  // the parent so the linter's no-unstable-nested-components rule is
  // satisfied and React does not tear down the button subtree on
  // every render. Same handler as the primary footer Save.

  if (loading || !draft) {
    return <div className="p-6 text-sm text-[#4B5773]">Loading…</div>;
  }

  const hasProfile = !!state.active_profile;
  const hasCells = (state.active_profile_cell_count || 0) > 0;
  const nextWeekHasLive = (state.next_window?.live_allocation_count || 0) > 0;
  const lastRun = state.last_run_result;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="rsa-page">
      <h1 className="text-2xl font-semibold text-[#0B1A36]">Rota Style Automation</h1>

      {/* TP-RSA.visibility (Feb 2026) -- when the last scheduler run
          could not create a draft, surface the reason as an amber
          banner near the top so a customer sees WHY nothing happened
          without having to scroll to the small footer note. Never
          shown when the last run succeeded (that stays in the
          footer summary below). */}
      {lastRun && lastRun.ok === false && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
             data-testid="rsa-last-run-skipped-banner">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <div className="font-medium">Last automatic run skipped</div>
            <div className="mt-1 leading-relaxed"
                 data-testid="rsa-last-run-skipped-reason">
              {lastRun.message || lastRun.reason || "No reason recorded."}
            </div>
            {state.last_run_at && (
              <div className="text-xs text-amber-800 mt-1 opacity-80">
                Ran at {humanDateTime(state.last_run_at)}. It will retry on the next scheduled slot once the cause is resolved.
              </div>
            )}
          </div>
        </div>
      )}

      {/* R3 (Feb 2026) - Automation V1 + Leave automation guidance
          blocks. The main Rota Style page never describes these; they
          live here on the Automation page only. */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
           data-testid="rsa-guidance-blocks">
        <div className="rounded-lg border border-[#E4EAF4] bg-white p-4"
             data-testid="rsa-guidance-automation-v1">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#DBE7FE] text-[#1D4ED8] grid place-items-center">
              <Zap className="h-4 w-4" />
            </div>
            <h3 className="font-medium text-[14px] text-[#0B1A36]">Automation V1</h3>
          </div>
          <p className="text-[12px] text-[#4B5773] mt-2 leading-snug">
            Create draft rosters from your Rota Style on a schedule.
            Command customers can also choose whether NextRota should
            publish the draft after a waiting period.
          </p>
        </div>
        <div className="rounded-lg border border-[#E4EAF4] bg-white p-4"
             data-testid="rsa-guidance-leave-automation">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#DCFCE7] text-[#166534] grid place-items-center">
              <CalendarCheck className="h-4 w-4" />
            </div>
            <h3 className="font-medium text-[14px] text-[#0B1A36]">Leave automation</h3>
          </div>
          <p className="text-[12px] text-[#4B5773] mt-2 leading-snug">
            Auto-approval can approve annual leave when the request
            stays within your daily absence limit. Other leave types
            still need manager review.
          </p>
        </div>
      </div>

      <p className="text-sm text-[#4B5773] mt-4 leading-relaxed">
        At the configured time each week, NextRota will create next week&apos;s
        roster from your Rota Style, notify admins, and auto-publish after the
        chosen waiting period. Admins can edit the roster anytime before
        auto-publish; if no one disables automation in time, it publishes
        automatically. No approval step is required.
      </p>

      {/* Empty-state guards before any toggles */}
      {!hasProfile && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
             data-testid="rsa-empty-no-profile">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              Create a Rota Style before enabling automation.
            </p>
            <Button asChild size="sm" variant="outline"
                    className="mt-2 border-amber-400 text-amber-900 hover:bg-amber-100">
              <a href="/app/rota-style">
                Go to Rota Style
                <ArrowRight className="h-3 w-3 ml-1" />
              </a>
            </Button>
          </div>
        </div>
      )}

      {hasProfile && !hasCells && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
             data-testid="rsa-empty-no-cells">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
          <p className="text-sm text-amber-900">
            Your Rota Style is empty. Add at least one assignment to the weekly
            grid before generating.
          </p>
        </div>
      )}

      {nextWeekHasLive && (
        <div className="mt-4 rounded-lg border border-[#CBE0FB] bg-[#EFF3FB] p-4 flex items-start gap-3"
             data-testid="rsa-warn-live-rota">
          <Info className="h-4 w-4 text-[#2563EB] mt-0.5" />
          <p className="text-sm text-[#0B1A36]">
            Week of {humanDate(state.next_window.window_start)} to{" "}
            {humanDate(state.next_window.window_end)} already has a published
            rota, so Automation V1 will prepare the next available week
            instead. This is normal after publishing your first roster.
          </p>
        </div>
      )}

      {/* Card 1 — Automatic weekly review draft creation */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white"
               data-testid="rsa-card-draft">
        <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-medium text-[#0B1A36]">
              Automatic weekly roster creation
            </h2>
            <p className="text-xs text-[#4B5773] mt-0.5">
              Creates next week&apos;s roster directly from your Rota Style. Admins
              can edit it before auto-publish.
            </p>
          </div>
          <Switch
            checked={draft.enabled}
            disabled={!hasProfile}
            onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
            data-testid="rsa-toggle-enabled" />
        </header>
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-[#4B5773] w-32">
              Generate on
            </label>
            <select className="text-sm border border-slate-300 rounded px-2 py-1 bg-white"
                    value={draft.generate_day}
                    onChange={(e) => setDraft({ ...draft,
                       generate_day: parseInt(e.target.value, 10) })}
                    data-testid="rsa-generate-day">
              {DAY_LABELS.map((d, i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
            <span className="text-xs text-[#4B5773]">at</span>
            <Input type="time"
                   value={draft.generate_time}
                   onChange={(e) => setDraft({ ...draft, generate_time: e.target.value })}
                   className="text-sm w-[110px]"
                   data-testid="rsa-generate-time" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-[#4B5773] w-32">
              Generate for
            </label>
            <span className="text-sm text-[#0B1A36] bg-slate-50 px-2 py-1 rounded border border-slate-200"
                  data-testid="rsa-generate-target">
              Next week
            </span>
            <span className="text-xs text-[#4B5773]">
              ({humanDate(state.next_window?.window_start)} to{" "}
              {humanDate(state.next_window?.window_end)})
            </span>
          </div>
          <InlineSave testid="rsa-save-btn-card-draft" onSave={save} saving={saving} />
        </div>
      </section>

      {/* Card 2 - Team scope mode (TP-RSA.simple-flow Feb 2026) */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white"
               data-testid="rsa-card-team-scope">
        <header className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-base font-medium text-[#0B1A36]">
            Team scope
          </h2>
          <p className="text-xs text-[#4B5773] mt-0.5">
            Choose how automation picks the Rota Style pattern for each team.
          </p>
        </header>
        <div className="px-4 py-4 space-y-2">
          {[
            { value: "hybrid",
              label: "Hybrid (recommended)",
              hint: "Use each team's own Rota Style if it has one; otherwise fall back to the company-wide Rota Style."},
            { value: "team_only",
              label: "Team Rota Style only",
              hint: "Only automate teams that have their own team-scoped Rota Style. Teams without one are skipped."},
            { value: "company_only",
              label: "Company-wide only",
              hint: "Use the company-wide Rota Style for every team. Team-scoped patterns are ignored."},
          ].map((opt) => (
            <label key={opt.value}
                   className="flex items-start gap-2 cursor-pointer rounded-md hover:bg-slate-50 px-2 py-1.5"
                   data-testid={`rsa-team-scope-${opt.value}`}>
              <input type="radio"
                     name="rsa-team-scope-mode"
                     className="mt-1"
                     checked={(draft.team_scope_mode || "hybrid") === opt.value}
                     onChange={() => setDraft({ ...draft,
                                                  team_scope_mode: opt.value })} />
              <div>
                <div className="text-sm text-[#0B1A36]">{opt.label}</div>
                <div className="text-xs text-[#4B5773]">{opt.hint}</div>
              </div>
            </label>
          ))}
          {(state?.team_profiles_count != null) && (
            <p className="text-[11px] text-[#4B5773] mt-1"
               data-testid="rsa-team-scope-hint">
              {state.team_profiles_count} team-scoped Rota Style pattern{
                state.team_profiles_count === 1 ? "" : "s"} active in this
              company.
            </p>
          )}
          <InlineSave testid="rsa-save-btn-card-team-scope" onSave={save} saving={saving} />
        </div>
      </section>

      {/* Card 3 - Publish delay hours (TP-RSA.simple-flow Feb 2026) */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white"
               data-testid="rsa-card-publish-delay">
        <header className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-base font-medium text-[#0B1A36]">
            Auto-publish delay
          </h2>
          <p className="text-xs text-[#4B5773] mt-0.5">
            Admins can edit the generated roster during this waiting period. If
            no one disables automation in time, it auto-publishes.
          </p>
        </header>
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-[#4B5773] w-32">
              Wait
            </label>
            <Input type="number"
                   min={1}
                   max={336}
                   value={draft.publish_delay_hours ?? 48}
                   onChange={(e) => setDraft({ ...draft,
                      publish_delay_hours: parseInt(e.target.value, 10) || 0 })}
                   className="text-sm w-[100px]"
                   data-testid="rsa-publish-delay-hours" />
            <span className="text-xs text-[#4B5773]">
              hours after generation, then publish
            </span>
          </div>
          <InlineSave testid="rsa-save-btn-card-publish-delay" onSave={save} saving={saving} />
        </div>
      </section>

      {/* Card 4 - Auto-approve annual leave (team-threshold rule) */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white"
               data-testid="rsa-card-auto-approve-annual-leave">
        <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="pr-4">
            <h2 className="text-base font-medium text-[#0B1A36]">
              Auto-approve annual leave
            </h2>
            <p className="text-xs text-[#4B5773] mt-0.5 leading-relaxed">
              Automatically approves annual leave only when the
              employee&apos;s team stays within the configured daily
              leave limit. If the limit would be exceeded, the request
              stays pending for manager review.
            </p>
          </div>
          <Switch
            checked={!!draft.auto_approve_annual_leave_enabled}
            onCheckedChange={(v) => setDraft({
              ...draft, auto_approve_annual_leave_enabled: v })}
            data-testid="rsa-toggle-auto-approve-annual-leave" />
        </header>
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-[#4B5773] w-32">
              Daily limit
            </label>
            <Input type="number"
                   min={1}
                   max={100}
                   value={draft.auto_approve_annual_leave_pct ?? 20}
                   onChange={(e) => setDraft({ ...draft,
                      auto_approve_annual_leave_pct:
                        parseInt(e.target.value, 10) || 0 })}
                   disabled={!draft.auto_approve_annual_leave_enabled}
                   className="text-sm w-[90px]"
                   data-testid="rsa-auto-approve-annual-leave-pct" />
            <span className="text-xs text-[#4B5773]">
              % of the team can be off on any given working day
            </span>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-[#4B5773] w-32">
              Minimum baseline
            </label>
            <Input type="number"
                   min={0}
                   max={999}
                   value={draft.auto_approve_annual_leave_min_off ?? 1}
                   onChange={(e) => setDraft({ ...draft,
                      auto_approve_annual_leave_min_off:
                        parseInt(e.target.value, 10) || 0 })}
                   disabled={!draft.auto_approve_annual_leave_enabled}
                   className="text-sm w-[90px]"
                   data-testid="rsa-auto-approve-annual-leave-min-off" />
            <span className="text-xs text-[#4B5773]">
              employee(s) - always permitted, even on tiny teams
            </span>
          </div>
          <p className="text-xs text-[#4B5773] flex items-start gap-2 pt-1">
            <Info className="h-3.5 w-3.5 text-[#4B5773] mt-0.5 shrink-0" />
            <span>
              Bank holidays are ignored. Sick, unexpected and other
              leave types still need a manager. Approved leaves can
              still be reviewed, amended or revoked from the Leave
              Approvals page.
            </span>
          </p>
          <InlineSave testid="rsa-save-btn-card-auto-approve-annual-leave" onSave={save} saving={saving} />
        </div>
      </section>

      {/* Footer -- primary Save. TP-RSA.visibility (Feb 2026) removed
          the "Run Now" green button per customer feedback (customers
          were not using it and the scheduled path is the authoritative
          route). Per-card InlineSave buttons above call this same
          `save` handler so a customer never has to scroll here. */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} data-testid="rsa-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Save
        </Button>
      </div>

      {lastRun && lastRun.ok && (
        <div className="mt-4 text-xs text-[#4B5773] flex items-start gap-2"
             data-testid="rsa-last-run">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5" />
          <span>
            Last run {humanDateTime(state.last_run_at)} -{" "}
            {`created roster with ${lastRun.created_count ?? lastRun.teams_generated ?? 0} ${lastRun.teams_generated != null ? "team(s)" : "shifts"}.`}
          </span>
        </div>
      )}
    </div>
  );
}
